import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useNativeApp } from '@/lib/hooks/use-native-app';
import type { DeeplinkData } from '@/lib/hooks/use-native-app';

export default function NativeDeeplinkHandler() {
  const { isNativeApp, isReady } = useNativeApp();
  const router = useRouter();

  useEffect(() => {
    if (!isReady) {
      return;
    }
    if (!isNativeApp) {
      return;
    }

    const handleDeeplink = (deeplinkData: DeeplinkData) => {
      try {
        const { deepLink, payload } = deeplinkData;
        
        if (!deepLink) {
          console.warn('[Deeplink] No deepLink provided in deeplink data');
          return;
        }

        // Parse the deep link URL
        let targetPath = deepLink;

        // If the deepLink is a full URL, extract the path (+ query/hash)
        if (deepLink.startsWith('http://') || deepLink.startsWith('https://')) {
          try {
            const url = new URL(deepLink);
            targetPath = url.pathname + url.search + url.hash;
          } catch (error) {
            // If URL parsing fails, use the original deepLink
            console.warn('[Deeplink] Failed to parse URL, using as-is:', error);
          }
        } else if (deepLink.startsWith('/')) {
          // Already a relative path, use as is
          targetPath = deepLink;
        } else {
          // Path without leading slash, add it
          targetPath = '/' + deepLink;
        }

        // Log for debugging
        console.log('[Deeplink] Handling deeplink:', {
          original: deepLink,
          targetPath,
          payload,
        });

        // Navigate to the deep link path
        router.push(targetPath);
      } catch (error) {
        console.error('[Deeplink] Error handling deeplink:', error);
      }
    };

    // Set up the callback for native app
    if (window.QualiAppNative) {
      window.QualiAppNative.onPushDeeplink = handleDeeplink;
    }

    // Listen for custom events dispatched by injected JavaScript
    const handlePushDeeplinkEvent = (event: CustomEvent<DeeplinkData>) => {
      handleDeeplink(event.detail);
    };

    // Listen for postMessage from ReactNativeWebView
    const handlePostMessage = (event: MessageEvent) => {
      try {
        // Only handle messages that look like our deeplink format
        if (typeof event.data === 'string') {
          const data = JSON.parse(event.data);
          if (data.type === 'pushDeeplink' && data.deepLink) {
            handleDeeplink(data);
          }
        }
      } catch (error) {
        // Ignore non-JSON messages or parsing errors
      }
    };

    window.addEventListener(
      'pushDeeplink',
      handlePushDeeplinkEvent as EventListener,
    );
    window.addEventListener('message', handlePostMessage);

    return () => {
      window.removeEventListener(
        'pushDeeplink',
        handlePushDeeplinkEvent as EventListener,
      );
      window.removeEventListener('message', handlePostMessage);

      if (window.QualiAppNative) {
        window.QualiAppNative.onPushDeeplink = undefined;
      }
    };
  }, [isReady, isNativeApp, router]);

  // This component doesn't render anything
  return null;
}

